import { Button, Group, Text } from '@mantine/core';
import { useState } from 'react';
import styled from 'styled-components';
import { uuidv7 } from 'uuidv7';
import { PALETTE } from '@dealstack/shared/lib/common/business/stampDesign';

import { DATE_DRAG_MIME } from './extensions/dateCommands';
import { PaletteList } from './flowDocumentEditor.styled';

interface Props {
	/** In document order. */
	placedIds: string[];
	/** Inserts at the caret. Returns false when a block cannot go where the caret is. */
	onInsert: (templateDatePlacementId: string) => boolean;
	onLocate: (templateDatePlacementId: string) => void;
	onRemove: (templateDatePlacementId: string) => void;
}

/**
 * Lists the document's date anchors. Each id is minted once per anchor, never derived from
 * position, because the date-stamp ledger references it.
 */
export const DateAnchorPalette = ({ placedIds, onInsert, onLocate, onRemove }: Props) => {
	const [declined, setDeclined] = useState(false);

	return (
		<>
			<Text size="xs" c="dark.0" mb="xs">
				{placedIds.length === 0
					? 'No date field yet. The date is written into the box after every signature has landed, so it needs somewhere to go.'
					: `${placedIds.length} date field${placedIds.length === 1 ? '' : 's'}. They are dated in the order they appear in the document.`}
			</Text>
			<AddRow
				data-testid="date-anchor-add"
				draggable
				onDragStart={(event) => {
					event.dataTransfer.setData(DATE_DRAG_MIME, uuidv7());
					event.dataTransfer.effectAllowed = 'copy';
				}}
			>
				<Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
					<Dot />
					<Text size="sm">Date field</Text>
				</Group>
				<Button variant="filled" size="xs" onClick={() => setDeclined(!onInsert(uuidv7()))}>
					Add
				</Button>
			</AddRow>
			{declined && (
				<Text size="xs" c="red" mb="xs" data-testid="date-anchor-declined">
					Put the cursor where the date should go, or drag it into the document.
				</Text>
			)}
			{placedIds.length > 0 && (
				<PaletteList data-testid="date-anchor-palette">
					{placedIds.map((id, index) => (
						<AnchorRow key={id} data-testid={`date-anchor-row-${id}`}>
							<Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
								<Dot $filled />
								<Text size="sm" truncate>
									{/* 1-based to match the server's `Date #1 …` messages. */}
									{`Date #${index + 1}`}
								</Text>
							</Group>
							<Group gap={4} wrap="nowrap">
								<Button variant="subtle" size="xs" onClick={() => onLocate(id)}>
									Locate
								</Button>
								<Button
									variant="subtle"
									color="red"
									size="xs"
									onClick={() => onRemove(id)}
								>
									Remove
								</Button>
							</Group>
						</AnchorRow>
					))}
				</PaletteList>
			)}
		</>
	);
};

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	width: 100%;
	padding: 0.5rem 0.75rem;
	border-radius: 4px;

	&:hover {
		background-color: ${({ theme }) => theme.v3.colors.gray[1]};
	}
`;

const AddRow = styled(Row)`
	cursor: grab;
	margin-bottom: 0.5rem;
	border: 1px dashed ${({ theme }) => theme.v3.colors.gray[4]};
`;

const AnchorRow = styled(Row)`
	cursor: default;
`;

const Dot = styled.span<{ $filled?: boolean }>`
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background-color: ${({ $filled }) => ($filled === true ? PALETTE.brand : 'transparent')};
	border: 1.5px solid
		${({ $filled, theme }) => ($filled === true ? PALETTE.brand : theme.v3.colors.gray[5])};
	flex-shrink: 0;
`;
